import { IconSparkles } from "@tabler/icons-react"
import { useCallback, useEffect, useState } from "react"
import { useParams, useSearchParams } from "react-router"
import styled from "styled-components"

import { parseNumber } from "@forento/core/utilities/number"

import Button from "~/components/Button"
import FullLoadingPage from "~/components/FullLoadingPage"
import { usePlatform } from "~/contexts/UserContext"
import NotFoundPage from "~/pages/main/NotFoundPage"
import trpc, { query } from "~/utilities/trpc"

import AiCourseEditChat from "./AiCourseEditChat"
import ChapterEditor from "./ChapterEditor"
import Sidebar from "./Sidebar"

const aiPanelCollapsedKey = "course-ai-panel-collapsed"

const CourseDetailPage: React.FC = () => {
	const courseId = parseNumber(useParams().courseId!)
	const platform = usePlatform()
	const [searchParams, setSearchParams] = useSearchParams()

	const canUseAi = platform?.enabledFeatures.includes("ai-generated-courses") ?? false

	const { data: course, error, refetch } = query.course.get.useQuery(courseId ?? -1, { refetchOnWindowFocus: false })
	const [selectedChapterId, setSelectedChapterId] = useState<number | null>(null)
	const [editPageId, setEditPageId] = useState<number | null>(null)
	const [isAiModalOpen, setAiModalOpen] = useState(false)
	const [isAiPanelExpanded, setAiPanelExpanded] = useState(() => localStorage.getItem(aiPanelCollapsedKey) !== "true")

	const handleAiPanelExpandedChange = useCallback((isExpanded: boolean) => {
		setAiPanelExpanded(isExpanded)
		localStorage.setItem(aiPanelCollapsedKey, (!isExpanded).toString())
	}, [])

	// A link into the chat expands it once, whatever the creator collapsed it to last time.
	useEffect(() => {
		if (searchParams.get("ai") !== "true") return
		setAiPanelExpanded(true)
		setSearchParams(
			current => {
				const next = new URLSearchParams(current)
				next.delete("ai")
				return next
			},
			{ replace: true },
		)
	}, [searchParams, setSearchParams])

	useEffect(() => {
		setSelectedChapterId(current => (current === null ? (course?.chapters[0]?.id ?? null) : current))
	}, [course])

	if (error) return <p>Failed to load course.</p>
	if (course === undefined) return <FullLoadingPage />
	if (course === null) return <NotFoundPage />

	return (
		<Container>
			<Sidebar
				course={{
					id: course.id,
					title: course.title,
					isPublished: course.publishDate !== null,
					chapters: course.chapters,
				}}
				reloadCourse={refetch}
				selectedChapterId={selectedChapterId}
				setSelectedChapterId={setSelectedChapterId}
				isAiModalOpen={isAiModalOpen}
				setAiModalOpen={setAiModalOpen}
				isAiPanelExpanded={isAiPanelExpanded}
				onAiPanelExpandedChange={handleAiPanelExpandedChange}
				onCreateChapterClick={async () => {
					const chapter = await trpc.course.createChapter.mutate({ courseId: course.id })
					await refetch()
					setSelectedChapterId(chapter.id)
				}}
			/>
			{selectedChapterId === null ? (
				<div className="flex w-full flex-col items-center gap-4 self-center">
					<p>Create a chapter to begin editing the course</p>
					{canUseAi && (
						<Button variant="primary" onClick={() => setAiModalOpen(true)}>
							<IconSparkles className="size-4" />
							Generate a chapter with Forento AI
						</Button>
					)}
				</div>
			) : (
				<ChapterEditor
					chapterId={selectedChapterId}
					editPageId={editPageId}
					setEditPageId={setEditPageId}
					reloadCourse={refetch}
					freePreview={
						course.price !== null || course.membershipIds.length > 0 || course.isManualOnly
							? {
									pagesBeforeChapter: course.chapters
										.slice(
											0,
											course.chapters.findIndex(chapter => chapter.id === selectedChapterId),
										)
										.reduce((sum, chapter) => sum + chapter.pages.length, 0),
									pageCount: course.freePreviewPageIds.length,
								}
							: null
					}
					onGenerateWithAi={() => setAiModalOpen(true)}
				/>
			)}
			{canUseAi && (
				<AiCourseEditChat
					isExpanded={isAiPanelExpanded}
					courseId={course.id}
					selectedChapterTitle={
						course.chapters.find(chapter => chapter.id === selectedChapterId)?.title ?? null
					}
					onExpandedChange={handleAiPanelExpandedChange}
				/>
			)}
		</Container>
	)
}

const Container = styled.div`
	min-height: 100vh;
	display: flex;
	background-color: white;
`

export default CourseDetailPage
